import { useEffect, useState, useCallback, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, Pencil, Save, X, RotateCcw, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { TigoLayout } from "./TigoLayout";

const ACCENT = "#3DA935";

// Dialog con layout interattivo + elenco dispositivi. `loader` è una funzione async
// che restituisce il payload layout (moduli + kpi). Aggiorna ogni 30s.
// Se `editable` e `systemId` sono forniti, abilita l'editor di disposizione (drag & drop).
export function TigoLayoutDialog({ title, loader, onClose, editable = false, systemId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editPos, setEditPos] = useState(null);
  const [saving, setSaving] = useState(false);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const load = useCallback(() => {
    setLoading(true);
    loaderRef.current().then((d) => setData(d)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(() => { if (!editing) load(); }, 30000);
    return () => clearInterval(id);
  }, [load, editing]);

  const startEdit = () => {
    const pos = {};
    (data?.moduli || []).forEach((m) => { pos[m.idx] = { row: m.row, col: m.col }; });
    setEditPos(pos);
    setEditing(true);
  };

  const movePanel = (idx, row, col) => {
    setEditPos((prev) => {
      const next = { ...prev };
      const occ = Object.keys(next).find((k) => +k !== idx && next[k].row === row && next[k].col === col);
      const old = next[idx];
      next[idx] = { row, col };
      if (occ != null) next[occ] = old;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const positions = Object.entries(editPos).map(([idx, p]) => ({ idx: +idx, row: p.row, col: p.col }));
      await api.post(`/tigo/systems/${systemId}/layout`, { positions });
      toast.success("Disposizione salvata");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await api.delete(`/tigo/systems/${systemId}/layout`);
      toast.success("Disposizione ripristinata");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operazione non riuscita");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={() => { if (editing) setEditing(false); else onClose(); }}>
      <DialogContent className="max-w-5xl border-neutral-800 bg-neutral-950 text-white" data-testid="tigo-layout-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 pr-8">
            <span className="flex h-6 w-6 items-center justify-center rounded-md" style={{ background: `${ACCENT}22`, color: ACCENT }}><Sparkles className="h-4 w-4" /></span>
            {title || "Ottimizzatori Tigo"}
            <span className="ml-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide" style={{ background: `${ACCENT}26`, color: ACCENT }}>Demo</span>
            {editable && systemId && (
              <div className="ml-auto flex items-center gap-2">
                {editing ? (
                  <>
                    <button type="button" onClick={reset} disabled={saving} data-testid="tigo-layout-reset" className="inline-flex items-center gap-1 rounded-md border border-neutral-700 px-2.5 py-1 text-xs font-semibold text-neutral-300 transition-colors hover:bg-white/5"><RotateCcw className="h-3.5 w-3.5" />Ripristina</button>
                    <button type="button" onClick={() => setEditing(false)} disabled={saving} data-testid="tigo-layout-cancel" className="inline-flex items-center gap-1 rounded-md border border-neutral-700 px-2.5 py-1 text-xs font-semibold text-neutral-300 transition-colors hover:bg-white/5"><X className="h-3.5 w-3.5" />Annulla</button>
                    <button type="button" onClick={save} disabled={saving} data-testid="tigo-layout-save" className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-bold text-white transition-transform hover:scale-105" style={{ background: ACCENT }}>{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}Salva</button>
                  </>
                ) : (
                  <button type="button" onClick={startEdit} data-testid="tigo-layout-edit" className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-bold transition-colors" style={{ borderColor: `${ACCENT}66`, color: ACCENT }}><Pencil className="h-3.5 w-3.5" />Modifica disposizione</button>
                )}
              </div>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[75vh] overflow-y-auto pr-1">
          <TigoLayout data={data} loading={loading} editing={editing} editPos={editPos} onMovePanel={movePanel} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
